import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useIntl } from '@/hooks';
import { useReadyInfo } from '@/providers/ReadyProvider';
import Image from '@/components/Bicester/Image';
import { Button, RsInput, RsPicker } from '@/components/Bicester';
import { Alert, Form, FormControl, FormGroup, Schema } from 'rsuite';
import { isFormValid } from '@/utils/form';
import { useRequest } from 'ahooks';
import { fetchServiceSiteList, fetchSite } from '@/services/basic';
import { fetchLogin, fetchSendVerify, fetchUserProfile, fetchVerify } from '@/services/login';
import { ShoppingVillage } from '@/constants';
import PageLoader from '@/components/shared/PageLoader';
import QrCode from './QrCode';

const TIME_COUNT = 60;

interface FormValueType {
  servicesite_id: number | string;
  user_name: string;
  password: string;
}

const Login = () => {
  const { formatMessage } = useIntl();
  const { setProfile } = useReadyInfo();
  const time = useRef(null);

  const [showValidatePage, setShowValidatePage] = useState(false); // 验证码界面
  const [showCodeInputPage, setShowCodeInputPage] = useState(false); // 输入验证码页面
  const [countDown, setCountDown] = useState(0);
  const [formError, setFormError] = useState({});
  const [formValue, setFormValue] = useState<FormValueType>({
    servicesite_id: null,
    user_name: '',
    password: '',
  });
  const [code, setCode] = useState('');

  // 获取服务站点列表
  const {
    run: getServiceSiteList,
    data: { service_site_list: serviceSiteListData = [] } = {},
    loading: serviceSiteListLoading,
  } = useRequest(fetchServiceSiteList, {
    manual: true,
  });
  // 获取网站基础数据的两个二维码
  const {
    run: getSite,
    data: siteData = { sh_wechat_url: '', bingding_url: '' },
    loading: siteLoading,
  } = useRequest(fetchSite, {
    manual: true,
  });
  // 登录
  const { run: login, loading: loginLoading } = useRequest(fetchLogin, {
    manual: true,
  });
  // 发送登录验证码
  const { run: sendVerify, loading: sendVerifyLoading } = useRequest(fetchSendVerify, {
    manual: true,
  });
  // 验证
  const { run: verify, loading: verifyLoading } = useRequest(fetchVerify, {
    manual: true,
  });
  // 获取账户详情
  const { run: getProfile, loading: getProfileLoading } = useRequest(fetchUserProfile, {
    manual: true,
  });

  const schemaModel = () => {
    return Schema.Model({
      servicesite_id: Schema.Types.NumberType().isRequired('请选择服务站点'),
      user_name: Schema.Types.StringType().isRequired('请输入用户名'),
      password: Schema.Types.StringType().isRequired('请输入密码'),
    });
  };
  const codeSchemaModel = () => {
    return Schema.Model({
      code: Schema.Types.StringType().isRequired('请输入验证码'),
    });
  };

  const handleLogin = () => {
    if (isFormValid(schemaModel(), formValue)) {
      login({
        data: formValue,
      }).then((res) => {
        if (!_.isNil(res.wechat_verify) && !_.isNil(res.is_binding)) {
          const { wechat_verify, is_binding } = res;
          setShowValidatePage(wechat_verify);

          // wechat_verify=true 需要验证
          if (wechat_verify) {
            // is_binding=false 需要先扫二维码绑定
            if (!is_binding) {
              getSite();
            }
            setShowCodeInputPage(is_binding);
          } else {
            handleGetProfile();
          }
        }
      });
    }
  };

  const handleSendCode = () => {
    sendVerify().then((res) => {
      Alert.success('发送成功');
      setCountDown(TIME_COUNT);
    });
  };

  const handleGetProfile = (needVerify = false) => {
    if (needVerify) {
      if (isFormValid(codeSchemaModel(), { code })) {
        verify(code).then((res) => {
          getProfile().then((res) => {
            if (res?.role) {
              Alert.success('登录成功');
              setProfile(res);
            }
          });
        });
      }
    } else {
      getProfile().then((res) => {
        if (res?.role) {
          Alert.success('登录成功');
          setProfile(res);
        }
      });
    }
  };

  useEffect(() => {
    getServiceSiteList();
  }, []);

  useEffect(() => {
    // 第一次自动发送验证码
    if (showCodeInputPage) {
      handleSendCode();
    }
  }, [showCodeInputPage]);

  useEffect(() => {
    if (countDown === TIME_COUNT) {
      time.current = setInterval(() => {
        setCountDown((countDown) => countDown - 1);
      }, 1000);
    } else if (countDown === 0) {
      clearInterval(time.current);
    }
  }, [countDown]);

  return (
    <div className="login">
      <Image src="/resources/images/login-bg.png" className="login-bg" />
      {serviceSiteListLoading || siteLoading ? (
        <PageLoader />
      ) : (
        <div className="login-container">
          {showValidatePage ? (
            showCodeInputPage ? (
              <div className="login-panel">
                <Form checkTrigger="blur" model={codeSchemaModel()}>
                  {countDown === 0 ? (
                    <Button
                      appearance="link"
                      className="w-[100%] h-[33px] text-[#fff] hover:text-[#fff] focus:text-[#fff] active:text-[#fff] active:focus:text-[#fff] mb-[30px]"
                      loading={sendVerifyLoading}
                      onClick={handleSendCode}
                    >
                      重新发送验证码
                    </Button>
                  ) : (
                    <div className="w-[100%] h-[33px] leading-[33px] text-[#fff] text-center mb-[30px]">
                      {countDown}s
                    </div>
                  )}
                  <FormGroup style={{ marginBottom: 30 }}>
                    <FormControl
                      name="code"
                      style={{ height: 33, textAlign: 'center' } as any}
                      accepter={RsInput}
                      placeholder="请输入从微信弹出的验证码"
                      value={code}
                      onChange={(code) => setCode(code)}
                    />
                  </FormGroup>
                  <Button
                    className="login-btn"
                    type="submit"
                    block
                    loading={verifyLoading || getProfileLoading}
                    onClick={() => handleGetProfile(true)}
                  >
                    <FormattedMessage id="commons.buttons.login" />
                  </Button>
                </Form>
              </div>
            ) : (
              <QrCode qrCodeUrls={siteData} onClick={() => setShowCodeInputPage(true)} />
            )
          ) : (
            <div className="login-panel">
              <Form
                formError={formError}
                formValue={formValue}
                checkTrigger="blur"
                model={schemaModel()}
                onCheck={setFormError}
              >
                <FormGroup style={{ height: 33, marginBottom: 30 }}>
                  <FormControl
                    name="servicesite_id"
                    accepter={RsPicker}
                    align='center'
                    data={serviceSiteListData.map((item) => ({
                      label: `${ShoppingVillage[item.shopping_village]}-${item.name}`,
                      value: item.id,
                    }))}
                    placeholder={formatMessage({ id: 'commons.labels.selectSite' })}
                    cleanable={false}
                    onChange={(siteId) => {
                      const nextFormValue = {
                        ...formValue,
                        servicesite_id: siteId,
                      };
                      setFormValue(nextFormValue);
                    }}
                  />
                </FormGroup>
                <FormGroup style={{ height: 33, marginBottom: 30 }}>
                  <FormControl
                    name="user_name"
                    style={{ textAlign: 'center' } as any}
                    accepter={RsInput}
                    className="h-full"
                    placeholder={formatMessage({ id: 'commons.labels.username' })}
                    onChange={(username) => {
                      const nextFormValue = {
                        ...formValue,
                        user_name: username,
                      };
                      setFormValue(nextFormValue);
                    }}
                  />
                </FormGroup>
                <FormGroup style={{ height: 33, marginBottom: 30 }}>
                  <FormControl
                    name="password"
                    style={{ textAlign: 'center' } as any}
                    accepter={RsInput}
                    className="h-full"
                    type="password"
                    placeholder={formatMessage({ id: 'commons.labels.password' })}
                    onChange={(password) => {
                      const nextFormValue = {
                        ...formValue,
                        password,
                      };
                      setFormValue(nextFormValue);
                    }}
                  />
                </FormGroup>
                <Button
                  className="login-btn"
                  type="submit"
                  block
                  loading={loginLoading}
                  onClick={handleLogin}
                >
                  <FormattedMessage id="commons.buttons.login" />
                </Button>
              </Form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Login;
